import {
    FetchState,
    HsabExpenseByExternalIdRequest,
    HsabExpenseCategory,
    HsabExpenseCategoryDisplay,
    HsabExpenseEvidenceResponse,
    HsabExpensePagedResponse, HsabExpenseReimbursementResponse,
    HsabExpenseRequest,
    HsabExpenseResponse,
    HsabExpenseState,
    HsabExpenseTypeResponse,
    UnreimbursedPage,
    WexClaimReceiptStatus,
    WexUploadStatus,
} from "@/shared/types";
import { defineStore } from "pinia";
import _ from "lodash";
import * as expenseService from "@/shared/api/expenseService";
import * as storeHelper from "@/shared/storeHelper";
import * as hsabExpenseCategoryUtility from "@/employee/Utilities/HsabExpenseCategoryUtility";
import Vue from "vue";
import { useHsabAccountsStore } from "@/employee/store/hsabAccounts";
import { useNotifierStore } from "@/shared/store/notifier";
import { useHsabExpenseEvidenceStore } from "@/employee/store/hsabExpenseEvidence";
import moment from "moment/moment";

const generateEmptyState = () => ({
    wexSyncStatus: false,
    wexSyncFetchState: FetchState.Uninitialized,
    availableExpenses: {
        fetchState: FetchState.Uninitialized,
        page: 1,
        pageSize: 10,
        totalRecords: 0,
        numberOfPages: 0,
        records: [],
    },
    submittedExpenses: {
        fetchState: FetchState.Uninitialized,
        page: 1,
        pageSize: 10,
        totalRecords: 0,
        numberOfPages: 0,
        records: [],
    },
    ClaimReceiptStatusThatAllowFileUpload: [
        WexClaimReceiptStatus.ReceiptNeeded,
        WexClaimReceiptStatus.NewReceiptNeeded,
        WexClaimReceiptStatus.ReceiptOverdue
    ],
    expenseTypesAndSubtypesByCategories: {},
    expenseTypesAndSubtypesByCategoriesFetchState: {},
    expensesByExternalIdDictionary: {},
    expensesByExternalIdFetchStateDictionary: {},
});

const initialState: HsabExpenseState = generateEmptyState();

export const useHsabExpenseStore = defineStore("hsabExpense", {
    state: (): HsabExpenseState => (_.cloneDeep(initialState)),
    getters: {
        availableExpensesLoaded: state => state.availableExpenses.fetchState === FetchState.Loaded,
        submittedExpensesLoaded: state => state.submittedExpenses.fetchState === FetchState.Loaded,
        availableExpensesHasPages: state => state.availableExpenses.totalRecords > state.availableExpenses.pageSize,
        submittedExpensesHasPages: state => state.submittedExpenses.totalRecords > state.submittedExpenses.pageSize,
        submittedExpensesPaged (): (page: number) => HsabExpenseResponse[] {
            return (page: number): HsabExpenseResponse[] => {
                if (page <= 0 || page > this.submittedExpenses.numberOfPages) {
                    return [];
                }
                return this.submittedExpenses.records.slice((page - 1) * this.submittedExpenses.pageSize,
                    page * this.submittedExpenses.pageSize);
            };
        },
        availableExpensesPaged (): (page: number) => HsabExpenseResponse[] {
            return (page: number): HsabExpenseResponse[] => {
                if (page <= 0 || page > this.availableExpenses.numberOfPages) {
                    return [];
                }
                return this.availableExpenses.records.slice((page - 1) * this.availableExpenses.pageSize,
                    page * this.availableExpenses.pageSize);
            };
        },
        isReceiptUploadAllowedForStatus: state => (receiptStatus: WexClaimReceiptStatus) => {
            return _.includes(state.ClaimReceiptStatusThatAllowFileUpload, receiptStatus);
        },
        isCategoryLoading: state => accountType =>
            state.expenseTypesAndSubtypesByCategoriesFetchState[accountType] === FetchState.Loading,
        isCategoryLoaded: state => accountType =>
            state.expenseTypesAndSubtypesByCategoriesFetchState[accountType] === FetchState.Loaded,
        getExpenseTypesAndSubtypesByCategory: state => category =>
            state.expenseTypesAndSubtypesByCategories[category] ?
                state.expenseTypesAndSubtypesByCategories[category] : [],
        getExpenseTypeByCategoryAndType: state => (category, expenseType) =>
            state.expenseTypesAndSubtypesByCategories[category] ?
                _.find(state.expenseTypesAndSubtypesByCategories[category],
                    x => x.expenseType === expenseType) : null,
        isExpenseByExternalIdLoading: state => (externalId: string) =>
            state.expensesByExternalIdFetchStateDictionary[externalId] === FetchState.Loading,
        getCategoriesForUsersPlans () {
            const accountStore = useHsabAccountsStore();
            const plansAndSubPlans = accountStore.allActivePlanTypesAndSubTypes;

            return hsabExpenseCategoryUtility.getAllDisplayCategories(plansAndSubPlans);
        },
        isAddingClaimReceiptsAllowed (): (claim: HsabExpenseReimbursementResponse) => boolean {
            return (claim: HsabExpenseReimbursementResponse): boolean => {
                const isExpenseSubmitted = claim && claim.externalReimbursementId;
                const isReceiptStatusAllowedToUpload = this.isReceiptUploadAllowedForStatus(
                    claim.claimReceiptStatus);
                const finalFilingDate = moment(claim.planYearFinalFilingDate);
                const today = moment();
                const isOnOrBeforeFinalFilingDate = today.diff(finalFilingDate) <= 0;

                return isExpenseSubmitted &&
                    isReceiptStatusAllowedToUpload &&
                    isOnOrBeforeFinalFilingDate;
            };
        }
    },
    actions: {
        setFromResponse (state: any, response: HsabExpensePagedResponse) {
            state.page = response.page;
            state.totalRecords = response.totalRecords;
            state.numberOfPages = response.numberOfPages;
            state.pageSize = response.pageSize;

            let sIndex = (state.page - 1) * state.pageSize;
            const rlen = response.records.length;
            for (let index = 0; index < rlen; index++, sIndex++) {
                Vue.set(state.records, sIndex, response.records[index]);
            }

            const slen = state.records.length;
            for (; sIndex < slen; sIndex++) {
                Vue.delete(state.records, sIndex);
            }

            state.fetchState = FetchState.Loaded;
        },
        async loadAvailableExpenses () {
            if (this.availableExpenses.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            return expenseService.getAvailableHsabExpenses(this.availableExpenses.page, this.availableExpenses.pageSize)
                .then(response => {
                    this.setFromResponse(this.availableExpenses, response.data);
                }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));

        },
        async loadSubmittedExpenses () {
            if (this.submittedExpenses.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            return expenseService.getSubmittedHsabExpenses(this.submittedExpenses.page, this.submittedExpenses.pageSize)
                .then(response => {
                    this.setFromResponse(this.submittedExpenses, response.data);
                }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));

        },
        loadPageAvailableExpenses (request: UnreimbursedPage) {
            if ((this.fetchState !== FetchState.Loaded || !request || request.page <= 0 || request.page >
                    this.numberOfPages)
                && !request.force) {
                return Promise.resolve();
            }

            const elist = this.availableExpensesPaged(request.page);
            const fullPage = _.every(elist, _.identity);
            if (elist.length && fullPage && !request.force) {
                this.page = request.page;
                return Promise.resolve();
            }

            return expenseService.getAvailableHsabExpenses(request.page, this.availableExpenses.pageSize)
                .then(response => {
                    this.setFromResponse(this.availableExpenses, response.data);
                }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadPageSubmittedExpenses (request: UnreimbursedPage) {
            if ((this.fetchState !== FetchState.Loaded || !request || request.page <= 0 || request.page >
                    this.numberOfPages)
                && !request.force) {
                return Promise.resolve();
            }

            const elist = this.submittedExpensesPaged(request.page);
            const fullPage = _.every(elist, _.identity);
            if (elist.length && fullPage && !request.force) {
                this.page = request.page;
                return Promise.resolve();
            }

            return expenseService.getSubmittedHsabExpenses(request.page, this.submittedExpenses.pageSize)
                .then(response => {
                    this.setFromResponse(this.submittedExpenses, response.data);
                }).catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        reloadPage () {
            this.loadPageAvailableExpenses({ force: true, page: this.availableExpenses.page });
            this.loadPageSubmittedExpenses({ force: true, page: this.availableExpenses.page });
        },
        CreateOrUpdateExpenseAndSubmitForReimbursement (expense: HsabExpenseRequest) {
            const evidenceStore = useHsabExpenseEvidenceStore();
            const { stagedFilesForUpload, ...request } = expense;

            const mapResponse = response => {
                return response.data;
            };

            const uploadFiles = (saveResponse: HsabExpenseResponse) => {
                if (stagedFilesForUpload && stagedFilesForUpload.length > 0) {
                    return evidenceStore.uploadFiles(saveResponse.hsabExpenseId, stagedFilesForUpload)
                        .then(uploadResponse => {
                            // todo display errors if any
                            return saveResponse;
                        });
                }
                return Promise.resolve(saveResponse);
            };

            const submitForReimbursement = (response: HsabExpenseResponse) => {
                return expenseService.submitHsabExpense(response.hsabExpenseId);
            };

            const reloadCurrentPageData = response => {
                this.reloadPage();
                return response.data;
            };

            return expenseService.createOrUpdateHsabExpense(request)
                .then(mapResponse)
                .then(uploadFiles)
                .then(submitForReimbursement)
                .then(reloadCurrentPageData)
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                    throw err;
                });
        },
        saveExpense (expense: HsabExpenseRequest): Promise<any> {
            const { stagedFilesForUpload, ...request } = expense;
            return expenseService.createOrUpdateHsabExpense(expense)
                .then(response => {
                    return response.data;
                })
                .then(saveResponse => {
                    const evidenceStore = useHsabExpenseEvidenceStore();
                    if (stagedFilesForUpload && stagedFilesForUpload.length > 0) {
                        return evidenceStore.uploadFiles(saveResponse.hsabExpenseId, stagedFilesForUpload)
                            .then(uploadResponse => {
                                // todo display errors if any
                                return saveResponse;
                            });
                    }
                    return Promise.resolve(saveResponse);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadExpenses () {
            this.loadAvailableExpenses();
            this.loadSubmittedExpenses();
        },
        syncWexExpenses (skipSync = false) {
            if (this.wexSyncStatus || skipSync) {
                return Promise.resolve();
            }
            const hsabAccountsStore = useHsabAccountsStore();
            if (hsabAccountsStore.notionalAccounts.length === 0) {
                this.wexSyncFetchState = FetchState.Loaded;
                this.wexSyncStatus = true;
                return Promise.resolve();
            }
            this.wexSyncFetchState = FetchState.Loading;

            return expenseService.syncHsabExpenses().then(response => {
                if (response.data) {
                    this.wexSyncStatus = true;
                    this.wexSyncFetchState = FetchState.Loaded;
                } else {
                    this.wexSyncFetchState = FetchState.Failed;
                }
            }).catch(err => {
                this.wexSyncFetchState = FetchState.Failed;

                return storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        async deleteExpense (expenseId: number) {
            const notifierStore = useNotifierStore();
            return expenseService.deleteHsabExpense(expenseId)
                .then(() => {
                    notifierStore.setSuccess("Expense deleted successfully.");
                    return this.loadPageAvailableExpenses({ force: true, page: this.availableExpenses.page });
                })
                .catch(() => {
                    notifierStore.setDanger("Unable to delete expense.");
                });
        },
        loadExpenseTypesByCategory (categoryDisplay: HsabExpenseCategoryDisplay) {
            if (this.expenseTypesAndSubtypesByCategoriesFetchState[categoryDisplay.category] === FetchState.Loaded) {
                return Promise.resolve();
            }
            Vue.set(this.expenseTypesAndSubtypesByCategoriesFetchState, categoryDisplay.category, FetchState.Loading);

            const hsaMedialType: HsabExpenseTypeResponse = {
                expenseType: "Medical Expenses",
                expenseSubTypes: ["Other Medical"]
            };
            const hsabAccountsStore = useHsabAccountsStore();

            const updateWithCustomOptions = (lst: HsabExpenseTypeResponse[]) => {
                // manually add in record for hsa+notional user if its not there already
                if (hsabAccountsStore.userHasActiveNotionalAccounts &&
                    hsabAccountsStore.userHasHsaAccount &&
                    categoryDisplay.category === HsabExpenseCategory.Medical &&
                    !lst.some(f => f.expenseType === hsaMedialType.expenseType)) {
                    lst.push(hsaMedialType);
                }

                // add in custom types and subtypes
                if (categoryDisplay.addTypesIntoSelector.length > 0) {
                    _.forEach(categoryDisplay.addTypesIntoSelector, t => {
                        if (!lst.some(x => x.expenseType === t.expenseType)) {
                            lst.unshift(t);
                        }
                    });
                }

                // remove undesired types based on category
                if (categoryDisplay.removeTypesFromSelector.length > 0) {
                    lst = lst.filter(x =>
                        !categoryDisplay.removeTypesFromSelector.some(tRemove => tRemove === x.expenseType));
                }
                return lst;
            };

            if (hsabAccountsStore.userHasHsaAccount && !hsabAccountsStore.userHasActiveNotionalAccounts) {
                // hsa only user, can't call wex api to get types
                const results = updateWithCustomOptions([hsaMedialType]);
                Vue.set(this.expenseTypesAndSubtypesByCategories, categoryDisplay.category, results);
                Vue.set(this.expenseTypesAndSubtypesByCategoriesFetchState, categoryDisplay.category, FetchState.Loaded);
                return Promise.resolve();
            } else {
                // user has notional accounts, call wex and then add in our custom results
                return expenseService.getHsabExpenseTypesByCategory(categoryDisplay.category)
                    .then(result => {
                        const results = updateWithCustomOptions(result.data);
                        Vue.set(this.expenseTypesAndSubtypesByCategories, categoryDisplay.category, results);
                        Vue.set(this.expenseTypesAndSubtypesByCategoriesFetchState, categoryDisplay.category, FetchState.Loaded);
                    }).catch(_ => {
                        Vue.set(this.expenseTypesAndSubtypesByCategoriesFetchState, categoryDisplay.category, FetchState.Failed);
                        const notifierStore = useNotifierStore();
                        notifierStore.setDanger("Unable to retrieve expense types and sub-types.");
                    });
            }


        },
        updateCachedExpenseEvidence (expenseId: number, evidence: HsabExpenseEvidenceResponse[]) {
            // todo param type ^
            const available: HsabExpenseResponse[] = this.availableExpenses.records;
            const submitted: HsabExpenseResponse[] = this.submittedExpenses.records;
            const availableIndex = _.findIndex(available, x => x.hsabExpenseId === expenseId);
            const submittedIndex = _.findIndex(submitted, x => x.hsabExpenseId === expenseId);

            let expense = null;
            if (availableIndex >= 0) {
                expense = this.availableExpenses.records[availableIndex];
            } else if (submittedIndex >= 0) {
                expense = this.submittedExpenses.records[submittedIndex];
            } else {
                return;
            }

            const attachmentNames = _.map(evidence, x => x.fileName);
            let isAnyAttachmentInFailedStatus = false;
            if (expense.isSubmitted) {
                isAnyAttachmentInFailedStatus = _.some(evidence, x => x.wexUploadStatus !== WexUploadStatus.Success);
            } else {
                isAnyAttachmentInFailedStatus = _.some(evidence, x => x.wexUploadStatus === WexUploadStatus.Failed);
            }

            Vue.set(expense, "expenseEvidence", evidence);
            Vue.set(expense, "isAnyAttachmentInFailedStatus", isAnyAttachmentInFailedStatus);
            Vue.set(expense, "attachmentNames", attachmentNames);
        },
        loadExpenseByExternalId (request: HsabExpenseByExternalIdRequest) {
            let externalId = null;
            if (request.externalReimbursementId) {
                externalId = request.externalReimbursementId;
            } else if (request.externalTransactionId) {
                externalId = request.externalTransactionId;
            } else {
                return Promise.resolve();
            }

            if (this.expensesByExternalIdFetchStateDictionary[externalId] === FetchState.Loading ||
                this.expensesByExternalIdFetchStateDictionary[externalId] === FetchState.Loaded) {
                return Promise.resolve();
            }

            Vue.set(this.expensesByExternalIdFetchStateDictionary, externalId, FetchState.Loading);

            return expenseService.getHsabExpenseByExternalId(request)
                .then(response => {
                    Vue.set(this.expensesByExternalIdDictionary, externalId, response.data);
                    Vue.set(this.expensesByExternalIdFetchStateDictionary, externalId, FetchState.Loaded);
                    return response.data;
                })
                .catch(_ => {
                    Vue.set(this.expensesByExternalIdFetchStateDictionary, externalId, FetchState.Failed);
                    return Promise.resolve();
                });
        }
    }
});